<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import AgentPermissionsModal from './AgentPermissionsModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { usePermissions } from 'dashboard/composables/usePermissions';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdminOrSuperAdmin, isSuperAdmin } = usePermissions();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const showPermissionsModal = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

// KPIs
const totalAgents = computed(() => agentList.value.length);
const activeAgents = computed(() => agentList.value.filter(a => a.confirmed).length);
const superAdminCount = computed(() => agentList.value.filter(a => a.super_admin).length);
const adminCount = computed(() => agentList.value.filter(a => a.role === 'administrator' && !a.super_admin).length);
const regularAgentsCount = computed(() => agentList.value.filter(a => a.role === 'agent').length);
const templatesCount = computed(() => customRoles.value?.length || 0);

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Ahora';
  const min = Math.floor(sec / 60);
  if (min < 60) return `Hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `Hace ${days}d`;
  return d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit' });
}

// Avatar gradient color por nombre (determinístico)
const gradients = [
  'from-blue-500 to-cyan-600',
  'from-purple-500 to-pink-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-red-600',
  'from-indigo-500 to-purple-600',
];
function avatarGradient(name) {
  if (!name) return gradients[0];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return gradients[h % gradients.length];
}
function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '??';
}
function roleBadge(agent) {
  if (agent.super_admin) {
    return { label: '⭐ SUPER ADMIN', cls: 'bg-amber-500/20 text-amber-700 dark:text-amber-300' };
  }
  if (agent.custom_role_id) {
    const cr = findCustomRole(agent);
    return { label: `📋 ${cr?.name || 'PLANTILLA'}`, cls: 'bg-purple-500/20 text-purple-700 dark:text-purple-300' };
  }
  if (agent.role === 'administrator') {
    return { label: 'ADMIN', cls: 'bg-blue-500/20 text-blue-700 dark:text-blue-300' };
  }
  return { label: 'AGENTE', cls: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300' };
}

const showEditAction = agent => {
  // Admin o super admin puede editar agentes (regla 2026-05-19 · Victor).
  // A un super_admin solo lo edita otro super_admin.
  if (!isAdminOrSuperAdmin.value) return false;
  if (agent.super_admin && !isSuperAdmin.value) return false;
  if (currentUserId.value === agent.id) return false;
  return true;
};

const showDeleteAction = agent => {
  // Admin o super admin puede eliminar agentes (regla 2026-05-19 · Victor).
  // Invariantes duros: nunca un super_admin, nunca uno mismo, nunca el último admin.
  if (!isAdminOrSuperAdmin.value) return false;
  if (agent.super_admin) return false;
  if (currentUserId.value === agent.id) {
    return false;
  }
  if (!agent.confirmed) {
    return true;
  }
  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

// Fase 2 · abrir modal de permisos granulares al click en row
// Admin o super_admin puede gestionar permisos (regla 2026-05-19)
const openPermissionsModal = agent => {
  if (!isAdminOrSuperAdmin.value) return;
  currentAgent.value = agent;
  showPermissionsModal.value = true;
};
const closePermissionsModal = () => { showPermissionsModal.value = false; };
const onPermissionsSaved = () => {
  showPermissionsModal.value = false;
  store.dispatch('agents/get'); // refresca la lista para reflejar cambios
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-1 p-0.5 bg-n-slate-3 dark:bg-n-slate-9 border border-n-slate-5 dark:border-n-slate-7 rounded-md">
              <span class="px-3 py-1 text-xs font-bold rounded bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900">📋 Lista</span>
              <router-link :to="{ name: 'agent_kanban', params: { accountId: $route.params.accountId } }" class="px-3 py-1 text-xs font-bold text-n-slate-11 hover:text-n-slate-12 cursor-pointer rounded">🗂 Kanban</router-link>
            </div>
            <Button
              v-if="isAdminOrSuperAdmin"
              :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
              size="sm"
              @click="openAddPopup"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- KPIs 6-col -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 pb-5 pt-1">
        <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
          <div class="text-2xl font-bold mt-1 text-slate-900 dark:text-slate-100">{{ totalAgents }}</div>
        </div>
        <div class="bg-white dark:bg-slate-900 border border-emerald-500/30 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold">✓ Verificados</div>
          <div class="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-300">{{ activeAgents }}</div>
        </div>
        <div class="bg-white dark:bg-slate-900 border border-amber-500/30 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">⭐ Super</div>
          <div class="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-300">{{ superAdminCount }}</div>
        </div>
        <div class="bg-white dark:bg-slate-900 border border-blue-500/30 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-blue-500 font-semibold">🛡️ Admins</div>
          <div class="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-300">{{ adminCount }}</div>
        </div>
        <div class="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agentes</div>
          <div class="text-2xl font-bold mt-1 text-slate-700 dark:text-slate-200">{{ regularAgentsCount }}</div>
        </div>
        <div class="bg-white dark:bg-slate-900 border border-purple-500/30 rounded-lg p-3">
          <div class="text-[10px] uppercase tracking-wider text-purple-500 font-semibold">📋 Plantillas</div>
          <div class="text-2xl font-bold mt-1 text-purple-600 dark:text-purple-300">{{ templatesCount }}</div>
        </div>
      </div>

      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>

      <!-- Tabla densa -->
      <div v-else class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <table class="w-full">
          <thead class="bg-slate-50 dark:bg-slate-950/50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th class="text-left px-4 py-2.5 font-semibold">Agente</th>
              <th class="text-left px-4 py-2.5 font-semibold">Email</th>
              <th class="text-left px-4 py-2.5 font-semibold">Rol</th>
              <th class="text-left px-4 py-2.5 font-semibold">Estado</th>
              <th class="text-left px-4 py-2.5 font-semibold">Última actividad</th>
              <th class="text-right px-4 py-2.5 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-800 text-sm">
            <tr
              v-for="(agent, index) in filteredAgentList"
              :key="agent.email"
              class="hover:bg-amber-500/10 dark:hover:bg-amber-500/10 transition-colors cursor-pointer"
              :title="`Click para gestionar permisos de ${agent.name}`"
              @click="openPermissionsModal(agent)"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <div
                    class="w-9 h-9 rounded-full bg-gradient-to-br flex items-center justify-center font-bold text-white text-xs overflow-hidden"
                    :class="avatarGradient(agent.name)"
                  >
                    <img v-if="agent.thumbnail" :src="agent.thumbnail" :alt="agent.name" class="w-full h-full object-cover" />
                    <span v-else>{{ getInitials(agent.name) }}</span>
                  </div>
                  <span class="font-medium text-slate-900 dark:text-slate-100 truncate">{{ agent.name }}</span>
                  <span class="text-[10px] text-amber-500 ml-auto">🔐 permisos</span>
                </div>
              </td>
              <td class="px-4 py-3 text-slate-500 dark:text-slate-400 truncate max-w-xs">{{ agent.email }}</td>
              <td class="px-4 py-3">
                <span class="text-[10px] font-bold px-2 py-1 rounded inline-block" :class="roleBadge(agent).cls">
                  {{ roleBadge(agent).label }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="text-xs flex items-center gap-1.5"
                  :class="agent.confirmed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'"
                >
                  <span
                    class="inline-block w-1.5 h-1.5 rounded-full"
                    :class="agent.confirmed ? 'bg-emerald-400' : 'bg-amber-400'"
                  />
                  {{ agent.confirmed ? $t('AGENT_MGMT.LIST.VERIFIED') : $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                </span>
              </td>
              <td class="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                {{ relativeTime(agent.last_activity_at || agent.last_sign_in_at) }}
              </td>
              <td class="px-4 py-3" @click.stop>
                <div class="flex justify-end gap-2">
                  <Button
                    v-if="showEditAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditPopup(agent)"
                  />
                  <Button
                    v-if="showDeleteAction(agent)"
                    v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[agent.id]"
                    @click="openDeletePopup(agent, index)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        :accessible-branch-ids="currentAgent.accessible_branch_ids || []"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <!-- Fase 2 · Modal de permisos granulares (M1.B + A3 layout) -->
    <AgentPermissionsModal
      v-if="showPermissionsModal && currentAgent.id"
      :agent="currentAgent"
      @close="closePermissionsModal"
      @saved="onPermissionsSaved"
    />
  </SettingsLayout>
</template>
